/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
import JSG from '@cedalo/jsg-ui';
import EditIcon from '@mui/icons-material/esm/Edit';
import { Checkbox, FormControlLabel, FormGroup, Paper, Stack } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import VisibilityOffIcon from '@mui/icons-material/esm/VisibilityOff';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import SettingsPaper from '../../utils/SettingsPaper';
import { graphManager } from '../../../GraphManager';
import { useJSGNotification } from '../../utils/JSGHooks';

export function ShapeListSettings(props) {
	const [forSheet, setForSheet] = useState(false);
	const [selected, setSelected] = useState();
	const [shapes, setShapes] = useState([]);
	const [random, setRandom] = useState(Math.random());
	const [sheetId, setSheetId] = useState(graphManager.graph.sheetSelection.sheetId);

	const getShapeVisible = shape => {
		if (!shape.attributes || shape.attributes.visible === undefined) {
			return true;
		}

		if (shape.attributes.visible.sv !== undefined) {
			return shape.attributes.visible.sv;
		}

		return !(shape.attributes.visible.v === false || shape.attributes.visible.v === 'false');

	}

	const getShapes = (id, onlySheet) => {
		const shapeList = [];

		JSG.streamSheets.forEach((sheetContainerModel, index) => {
			const { sheet } = sheetContainerModel;
			if (onlySheet === false || sheet.id === sheetId) {
				sheet.shapes.shapes.forEach((shape) => {
					if (shape.itemType !== 'layoutcell' && shape.itemType !== 'layoutnode') {
						shapeList.push({
							name: shape.name ? shape.name.v : 'NotDefined',
							sheet: sheet.name,
							visible: getShapeVisible(shape),
							sheetId: sheet.id,
							sheetIndex: index
						});
					}
				});
			}
		});
		setSelected('');

		return shapeList;
	};

	// const getShapeByName = (name) => {
	// 	return JSG.streamSheets.find((sheetContainerModel) => {
	// 		const { sheet } = sheetContainerModel;
	// 		return sheet.shapes.shapes.find((shape) => shape.name && shape.name.v === name);
	// 	});
	// };

	useEffect(() => {
		setShapes(getShapes(sheetId, forSheet));
	}, [props.open, sheetId, random]);

	const onSelectionChange = () => {
		setSheetId(() => graphManager.graph.sheetSelection.sheetId);
	}

	const onShapeChange = () => {
		setRandom(() => Math.random());
	}

	useJSGNotification('SHAPE_NOTIFICATION', onShapeChange, [random]);
	useJSGNotification(JSG.WorksheetNode.SELECTION_CHANGED_NOTIFICATION, onSelectionChange, [sheetId]);

	// const handleDelete = () => {
		// const nameItem = JSG.sheetNames.get(selected);
		// if (nameItem) {
		// 	const cmd = SheetCommandFactory.create('command.DeleteSheetNameCommand', graphManager.graph, nameItem);
		// 	graphManager.synchronizedExecute(cmd);
		// }
	// };

	const handleClose = () => {
		props.close();
	};

	const handleShow = (event, shape) => {
		// activate sheet, if necessary
		const viewer = graphManager.getGraphViewer();

		props.onSheetActivate(sheetId);
		graphManager.graph.streamSheetsContainer.leftSheetContainer.setActiveSheetIndex(shape.sheetIndex, viewer);

		const item = graphManager.graph.streamSheetsContainer.leftSheetContainer.getItemByName(shape.name);
		if (!item) {
			return;
		}

		// select
		const controller = viewer.getRootController().getControllerByModelId(item.getId());
		if (!controller) {
			return;
		}
		viewer.getSelectionProvider().setSelection([controller]);
		viewer.getSelectionView().refresh();

		let view = controller.getView().getParent();
		while (!(view instanceof JSG.WorksheetView)) {
			view = view.getParent();
		}
		// scroll into view
		const rect = item.getTranslatedBoundingBox(view.getItem()).getBoundingRectangle();
		const wsView = view.getWorksheetView();
		wsView.showRect(wsView.getItem(), rect);
		graphManager.redraw();
	};

	const handleEdit = (event, shape) => {
		handleShow(event, shape);
		JSG.NotificationCenter.getInstance().send(
			new JSG.Notification(JSG.SHOW_SHAPE_SETTINGS, {open: true})
		);
	}

	if (!props.open) {
		return null;
	}

	return (
		<SettingsPaper
			open={props.open}
			close={handleClose}
			helpContext="guides/app/shapes"
			title={<FormattedMessage id="ShapeListSettings.Title" defaultMessage="Shape List" />}
		>
			<FormGroup>
				<Stack direction="row" spacing={1} sx={{flexDirection: 'row-reverse'}}>
					{/*<IconButton onClick={handleDelete} size="small" disabled={!selected}>*/}
					{/*	<DeleteIcon/>*/}
					{/*</IconButton>*/}
					<IconButton onClick={(event) => handleEdit(event, selected)} size="small" disabled={!selected}>
						<EditIcon/>
					</IconButton>
				</Stack>
				<Paper
					variant="outlined"
					style={{
						marginTop: '10px',
						height: '500px',
						overflowY: 'scroll'
					}}
				>
					<Table stickyHeader size="small">
						<TableHead>
							<TableRow>
								<TableCell/>
								<TableCell>
									<FormattedMessage id="ShapeListSettings.Name" defaultMessage="Name" />
								</TableCell>
								<TableCell>
									<FormattedMessage id="ShapeListSettings.Sheet" defaultMessage="Sheet" />
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{shapes.map((shape) => (
								<TableRow
									onClick={() => setSelected(shape)}
									onDoubleClick={(event) => handleShow(event, shape)}
									selected={selected === shape}
									key={`${shape.name.v || ''}${Math.random()}`}
									sx={{ cursor: 'default', '&:last-child td, &:last-child th': { border: 0 } }}
								>
									<TableCell sx={{userSelect: 'none', padding: '5px 0px 0px 10px'}} key={1}>
										{shape.visible ? null : <VisibilityOffIcon />}
									</TableCell>
									<TableCell sx={{userSelect: 'none'}} key={2}>
										{shape.name}
									</TableCell>
									<TableCell sx={{userSelect: 'none'}} key={3}>
										{shape.sheet}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Paper>
			</FormGroup>
				<FormControlLabel
					sx={{
						marginTop: '10px'
					}}
					control={
						<Checkbox checked={forSheet} onChange={(event, state) => {
							setForSheet(state);
							setShapes(getShapes(sheetId, state));
						}} />
					}
					label={
						<FormattedMessage id="ShapeListSettings.OnlySheet" defaultMessage="Only for selected sheet" />
					}
				/>
		</SettingsPaper>
	);
}
